import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

export interface LegendItem {
  label: string;
  color: string;
}

/** White dashboard card with a title and short description. */
export function SectionCard({
  title,
  description,
  children,
  action,
  className = '',
}: {
  title: string;
  description?: string;
  children: ReactNode;
  /** Shown top right, e.g. a View All link. */
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={`h-full min-h-0 border-gray-200/80 bg-white shadow-sm ${className}`}>
      <CardContent className="flex h-full min-h-0 flex-col p-5">
        <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] text-gray-500">{description}</p>}
          </div>
          {action}
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </CardContent>
    </Card>
  );
}

/** Centered legend under a chart. */
export function ChartLegend({ items }: { items: LegendItem[] }) {
  return (
    <div className="mt-2 flex shrink-0 flex-wrap items-center justify-center gap-5 text-[13px] text-gray-600">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/** Tooltip box used by the bar and area charts. */
export function ChartTooltipBox({ title, rows }: { title: string; rows: (LegendItem & { value: string })[] }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg">
      <p className="mb-2 font-semibold text-gray-900">{title}</p>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-8">
            <span className="flex items-center gap-1.5 text-gray-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: row.color }} />
              {row.label}
            </span>
            <span className="font-semibold tabular-nums text-gray-900">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const BAR_TONES = {
  brand: '[&>div]:bg-[#4369a0]',
  emerald: '[&>div]:bg-emerald-500',
  amber: '[&>div]:bg-amber-500',
  teal: '[&>div]:bg-teal-600',
} as const;

/** Label and value on one line, with an optional progress bar below. */
export function MetricRow({
  label,
  value,
  progress,
  tone = 'brand',
}: {
  label: string;
  value: string;
  progress?: number;
  tone?: keyof typeof BAR_TONES;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold tabular-nums text-gray-900">{value}</span>
      </div>
      {progress !== undefined && <Progress value={progress} className={`h-2 bg-gray-100 ${BAR_TONES[tone]}`} />}
    </div>
  );
}

/** Small "nothing to show" text inside a card. */
export function EmptyText({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-sm text-gray-500">{children}</p>;
}

export const percentOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
